import { ChangeEvent, useState } from 'react';
import FormField from '@/common/PassengerTools/FormField';
import { Button as StyledButton, Select } from '../PassengerTools.styles';
import { addSoftPullToCurrentUser } from '../utils/createTestUser';

type OwnProps = {
  addCoSigner: boolean;
  applyOnCreate: boolean;
  creditStatus: 'Excellent' | 'Fair';
  expired: boolean;
  isLoggedIn: boolean;
  isOpen: boolean;
  onAddCoSignerChange: (addCoSigner: boolean) => void;
  onApplyOnCreateChange: (apply: boolean) => void;
  onCreditStatusChange: (status: 'Excellent' | 'Fair') => void;
  onExpiredChange: (expired: boolean) => void;
  onOpenChange: (open: boolean) => void;
};

export default function SoftPullSection({
  addCoSigner,
  applyOnCreate,
  creditStatus,
  expired,
  isLoggedIn,
  isOpen,
  onAddCoSignerChange,
  onApplyOnCreateChange,
  onCreditStatusChange,
  onExpiredChange,
  onOpenChange
}: OwnProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  async function handleAddSoftPull(reload: boolean) {
    setIsAdding(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await addSoftPullToCurrentUser({
        creditStatus,
        expired,
        withCoSigner: addCoSigner
      });
      setSuccessMessage(
        `Soft pull applied successfully${expired ? ' (expired)' : ''}${
          addCoSigner ? ' with co-signer' : ''
        }.`
      );
      if (reload) window.location.reload();
    } catch (error) {
      setErrorMessage(`Failed to apply soft pull: ${error}`);
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="flex flex-col items-start">
      <button
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="inline-flex items-center gap-8 rounded bg-gray-600 px-lg py-md text-white hover:bg-gray-700"
        data-analytics-id={null}
        data-testid="toggle-soft-pull"
      >
        <span className="text-sm font-medium">Soft Pull</span>
        {successMessage && !isOpen && (
          <span className="text-xs text-green-400">&#10003; Applied</span>
        )}
        <span className="text-sm">{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && (
        <div className="self-stretch flex flex-col gap-8 border border-gray-600 rounded-b p-sm">
          {!isLoggedIn && (
            <label
              htmlFor="softpull-apply-on-create"
              className="flex items-center gap-8 text-sm text-white"
            >
              <input
                type="checkbox"
                id="softpull-apply-on-create"
                checked={applyOnCreate}
                onChange={e => onApplyOnCreateChange(e.target.checked)}
                className="h-16 w-16"
              />
              Apply on Create
            </label>
          )}
          <FormField label="Credit Status" htmlFor="softpull-credit-status">
            <Select
              id="softpull-credit-status"
              value={creditStatus}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onCreditStatusChange(e.target.value as 'Excellent' | 'Fair')
              }
            >
              <option value="Excellent">Excellent</option>
              <option value="Fair">Fair</option>
            </Select>
          </FormField>

          <div className="flex flex-wrap items-center gap-16">
            <label
              htmlFor="softpull-expired"
              className="flex items-center gap-8 text-white"
            >
              <input
                type="checkbox"
                id="softpull-expired"
                checked={expired}
                onChange={e => onExpiredChange(e.target.checked)}
                className="h-16 w-16"
              />
              Expired Softpull
            </label>
            <label
              htmlFor="softpull-add-cosigner"
              className="flex items-center gap-8 text-white"
            >
              <input
                type="checkbox"
                id="softpull-add-cosigner"
                checked={addCoSigner}
                onChange={e => onAddCoSignerChange(e.target.checked)}
                className="h-16 w-16"
              />
              Add Co-Signer
            </label>
          </div>

          {successMessage && (
            <div className="text-green-400 text-sm">{successMessage}</div>
          )}
          {errorMessage && (
            <div className="text-[#feaaaa] text-sm">{errorMessage}</div>
          )}

          {isLoggedIn && (
            <div className="flex flex-wrap gap-8">
              <StyledButton
                type="button"
                onClick={() => handleAddSoftPull(false)}
                data-analytics-id={null}
                disabled={isAdding}
                data-testid="add-soft-pull"
              >
                {isAdding ? 'Applying...' : 'Apply Soft Pull'}
              </StyledButton>
              <StyledButton
                type="button"
                onClick={() => handleAddSoftPull(true)}
                data-analytics-id={null}
                disabled={isAdding}
                data-testid="add-soft-pull-and-update"
              >
                {isAdding ? 'Applying...' : 'Apply Soft Pull & Update'}
              </StyledButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
